/**
 * A vehicle's model taking a crash: its metal crushed and folded, its lamps
 * cracked, its glass crazed, and pieces broken off to fall in the road.
 *
 * The rules are in `core/vehicle/damage.ts` and the mesh work in
 * `core/vehicle/mesh.ts`; this applies them to a model's own meshes. A mesh
 * is made ready only the first time a crush comes near it: welded, cut finely
 * enough to fold, and split into the pieces that can break off it. Panels
 * (anything bigger than a hand or two) bend vertex by vertex. Small parts
 * (lamps, mirrors, plates, badges) are moved whole by the metal under them, so
 * they sit crooked, and break off when it moves far enough. A bumper or a
 * grille bends with the rest, and goes too when pushed well in.
 *
 * Every crush is kept (`VehicleDamage` in `core/vehicle/damage.ts`), and a
 * fresh model given the same crushes in order ends up the same: the dice for
 * which parts go are seeded by the crush.
 */

import type { BufferGeometry, Material, Object3D } from 'three';
import { BufferAttribute, BufferGeometry as Geometry, Color, Matrix3, Matrix4, Mesh, MeshStandardMaterial, Quaternion, Vector3 } from 'three';
import type { Build, Crush, Vec3, Zone } from '../core/vehicle/damage.js';
import { GLASS_CRAZES, LAMP_BREAKS, SCRAPE_OFF, breaksOff, crushField, dice, zoneLimit } from '../core/vehicle/damage.js';
import type { TriMesh } from '../core/vehicle/mesh.js';
import { pieces, tessellate, weld } from '../core/vehicle/mesh.js';
import { motoPaint, motoWhite } from './Motorbike.js';
import { scooterPaint } from './Scooter.js';
import { chrome, clusterGeometry, exhaustGeometry, glass, plate, plateBlue, trim } from './Vehicle.js';

/**
 * How finely a build's panels are cut where they fold, metres: a pleat
 * needs two or three edges across it. Around the fold, where the metal only
 * bends, a few times coarser; elsewhere they are left as they were.
 */
const EDGE: Record<Build, number> = { car: 0.075, truck: 0.1, moto: 0.03 };
const BEND = 2.5;
/** A piece no bigger than this, metres, is a part moved whole; bigger, a panel bent vertex by vertex. */
const SMALL: Record<Build, number> = { car: 0.6, truck: 0.7, moto: 0.22 };
/** Geometries shared between models: never thrown away. */
const SHARED = new Set<BufferGeometry>([exhaustGeometry, clusterGeometry]);

/** A lamp's glass broken: dark, unlit, its colour all but gone. */
const brokenLamp = new MeshStandardMaterial({ color: new Color(0x2b2c2e), roughness: 0.55, metalness: 0.2, flatShading: true });
const brokenTail = new MeshStandardMaterial({ color: new Color(0x3a1414), roughness: 0.55, metalness: 0.2, flatShading: true });
/** Glass crazed by a crash: white with cracks, no longer clear. */
const crazed = new MeshStandardMaterial({
  color: new Color(0xc9d2d8), roughness: 0.85, metalness: 0.1, flatShading: true, transparent: true, opacity: 0.82,
});

/** A piece of a prepared mesh: what it is, where, and whether it is gone. */
interface Part {
  /** Its triangles, by index into the mesh's index buffer / 3. */
  triangles: number[];
  /** Its vertices, each once. */
  vertices: number[];
  /** Its middle, in the mesh's frame, and its size. */
  centre: Vector3;
  size: number;
  /** Moved whole rather than bent. */
  small: boolean;
  gone: boolean;
  /** Its number among all the model's parts, for the dice. */
  serial: number;
}

interface Prepared {
  mesh: Mesh;
  /** Where its vertices began, in its own frame. */
  rest: Float32Array;
  parts: Part[];
  /** Which part each vertex is in. */
  partOf: Int32Array;
  lamp: 'head' | 'tail' | undefined;
  glass: boolean;
  /** Trim, chrome: bumpers and grilles, which may come off. */
  loose: boolean;
  /** A number plate. */
  plate: boolean;
}

/** A piece broken off: drawn on its own now, where it was, to be thrown. */
export interface BrokenPiece {
  mesh: Mesh;
  /** Its size, metres: half extents along its own axes. */
  half: Vec3;
}

const toRoot = new Matrix4();
const fromRoot = new Matrix3();
const rootInverse = new Matrix4();
const v = new Vector3();
const d = new Vector3();
const field: Vec3 = [0, 0, 0];
const inverseScratch = new Matrix4();

/** The inverse of a mesh-to-root matrix: root to mesh. */
function rootInverseOf(m: Matrix4): Matrix4 {
  return inverseScratch.copy(m).invert();
}

/** Whether a material is a lit lamp's, and which kind. */
function lampOf(material: Material): 'head' | 'tail' | undefined {
  if (!(material instanceof MeshStandardMaterial) || material.emissiveIntensity <= 0) return undefined;
  if (material.emissive.r + material.emissive.g + material.emissive.b < 0.05) return undefined;
  // A tail lamp glows red; a headlamp near white, an indicator amber.
  return material.emissive.g < 0.3 ? 'tail' : 'head';
}

export class Wreck {
  private readonly prepared = new Map<Mesh, Prepared>();
  private serial = 0;
  /** Which of its headlamps are broken, left then right: their beams stay dark. */
  readonly brokenBeams: [boolean, boolean] = [false, false];
  /** How far out its body reaches either side, metres: for what sticks out to be scraped off. */
  private halfWidth = 0;

  constructor(
    private readonly root: Object3D,
    private readonly build: Build,
    /** Parts never crushed: wheels, which turn on their own. */
    private readonly skip: ReadonlySet<Object3D>,
  ) {}

  /**
   * Apply a crush. `scrape` is how fast the vehicle slid along what it hit,
   * m/s, and `zone` where; a mirror on that side goes at speed. Returns the
   * pieces broken off, posed in the scene where they were.
   */
  hit(c: Crush, zone: Zone, scrape: number): BrokenPiece[] {
    this.root.updateMatrixWorld(true);
    rootInverse.copy(this.root.matrixWorld).invert();
    const reach = c.radius + c.zone + 0.3;
    const centre = new Vector3(...c.point);
    const broken: BrokenPiece[] = [];
    const limit = zoneLimit(zone, this.build) * 1.25;
    for (const mesh of this.meshes()) {
      toRoot.multiplyMatrices(rootInverse, mesh.matrixWorld);
      if (!this.near(mesh, centre, reach)) continue;
      const prep = this.prepare(mesh);
      fromRoot.setFromMatrix4(toRoot).invert();
      this.refine(prep, c);
      const pos = mesh.geometry.getAttribute('position') as BufferAttribute;
      let moved = false, glassShift = 0;
      for (const part of prep.parts) {
        if (part.gone) continue;
        let shift = 0;
        if (part.small) {
          // Moved whole, by the metal under its middle.
          v.copy(part.centre).applyMatrix4(toRoot);
          crushField(c, v.x, v.y, v.z, field);
          d.set(field[0], field[1], field[2]);
          shift = d.length();
          if (shift < 1e-4) continue;
          d.applyMatrix3(fromRoot);
          for (const i of part.vertices) this.move(pos, prep.rest, i, d.x, d.y, d.z, limit);
          part.centre.add(d);
        } else {
          for (const i of part.vertices) {
            v.fromBufferAttribute(pos, i).applyMatrix4(toRoot);
            crushField(c, v.x, v.y, v.z, field);
            const s = Math.hypot(field[0], field[1], field[2]);
            if (s < 1e-4) continue;
            shift = Math.max(shift, s);
            d.set(field[0], field[1], field[2]).applyMatrix3(fromRoot);
            this.move(pos, prep.rest, i, d.x, d.y, d.z, limit);
          }
        }
        if (shift === 0) continue;
        moved = true;
        glassShift = Math.max(glassShift, shift);
        if (prep.lamp !== undefined && shift >= LAMP_BREAKS) this.breakLamp(prep, part);
        // Small parts go at a few centimetres; trim panels (bumpers, grilles)
        // well pushed in; a motorbike's plastics, clipped on, as easily as a lamp.
        const large = !part.small && this.build !== 'moto';
        const roll = dice(c.seed, part.serial);
        const scraped = part.small && scrape > SCRAPE_OFF && this.sticksOut(prep, part, zone);
        // A motorbike's frame, engine and seat stay on it, however crushed:
        // only its plastics, lamps and plates come away.
        const comesOff = prep.loose || prep.lamp !== undefined || prep.plate || (part.small && this.build !== 'moto');
        if (comesOff && (breaksOff(shift, large, roll) || scraped)) {
          broken.push(this.breakOff(prep, part));
        }
      }
      if (!moved) continue;
      if (prep.glass && glassShift >= GLASS_CRAZES) mesh.material = crazed;
      pos.needsUpdate = true;
      this.dropGone(prep);
      mesh.geometry.computeVertexNormals();
      mesh.geometry.computeBoundingBox();
      mesh.geometry.computeBoundingSphere();
    }
    return broken;
  }

  /** Every mesh that can be crushed: not inside the cabin, not a wheel. */
  private meshes(): Mesh[] {
    const out: Mesh[] = [];
    const walk = (o: Object3D) => {
      if (this.skip.has(o) || o.userData.interior === true) return;
      if (o instanceof Mesh && !(o.userData.debris === true)) out.push(o);
      for (const child of o.children) walk(child);
    };
    walk(this.root);
    if (this.halfWidth === 0) {
      for (const mesh of out) {
        mesh.geometry.computeBoundingBox();
        const box = mesh.geometry.boundingBox!;
        toRoot.multiplyMatrices(rootInverse, mesh.matrixWorld);
        for (const x of [box.min.x, box.max.x]) {
          v.set(x, (box.min.y + box.max.y) / 2, (box.min.z + box.max.z) / 2).applyMatrix4(toRoot);
          this.halfWidth = Math.max(this.halfWidth, Math.abs(v.x));
        }
      }
    }
    return out;
  }

  /** Whether a mesh comes within `reach` of the crush's contact. */
  private near(mesh: Mesh, centre: Vector3, reach: number): boolean {
    const geometry = mesh.geometry;
    if (geometry.boundingSphere === null) geometry.computeBoundingSphere();
    const sphere = geometry.boundingSphere!;
    v.copy(sphere.center).applyMatrix4(toRoot);
    const scale = Math.max(...toRoot.toArray().slice(0, 3).map(Math.abs), 1);
    return v.distanceTo(centre) <= reach + sphere.radius * scale;
  }

  /** Make a mesh ready to crush, once: welded, and told into pieces. */
  private prepare(mesh: Mesh): Prepared {
    const known = this.prepared.get(mesh);
    if (known !== undefined) return known;
    const old = mesh.geometry;
    const welded = weld(old.getAttribute('position').array, old.index?.array);
    const geometry = new Geometry();
    geometry.setAttribute('position', new BufferAttribute(welded.positions, 3));
    geometry.setIndex(new BufferAttribute(welded.indices, 1));
    geometry.computeVertexNormals();
    mesh.geometry = geometry;
    if (!SHARED.has(old)) old.dispose();

    const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    const standard = material instanceof MeshStandardMaterial ? material : undefined;
    const prep: Prepared = {
      mesh, rest: welded.positions.slice(), parts: [], partOf: new Int32Array(0), lamp: lampOf(material),
      glass: material === glass || (standard?.transparent === true && standard.opacity < 0.6),
      // Bumpers, grilles and rails, bolted and clipped on; a motorbike's plastics.
      loose: material === trim || material === chrome || material === motoPaint || material === motoWhite || material === scooterPaint,
      plate: material === plate || material === plateBlue,
    };
    this.split(prep, welded);
    this.prepared.set(mesh, prep);
    return prep;
  }

  /**
   * Tell a mesh's parts apart again: after it is cut finer the same parts
   * are there, with more triangles. Each keeps its own story (its number for
   * the dice, whether it is small) by a vertex it had before.
   */
  private split(prep: Prepared, mesh: TriMesh): void {
    const { of, count } = pieces(mesh);
    const before = prep.parts, partOf = prep.partOf;
    const parts: Part[] = new Array(count);
    const p = mesh.positions, idx = mesh.indices;
    for (let t = 0; t < of.length; t++) {
      const k = of[t];
      if (parts[k] === undefined) {
        // The part this was: by the first vertex it shares with before.
        const first = idx[t * 3];
        const was = first < partOf.length && partOf[first] >= 0 ? before[partOf[first]] : undefined;
        parts[k] = was === undefined
          ? { triangles: [], vertices: [], centre: new Vector3(), size: 0, small: true, gone: false, serial: this.serial++ }
          : { ...was, triangles: [], vertices: [] };
        parts[k].triangles = [];
      }
      parts[k].triangles.push(t);
    }
    prep.partOf = new Int32Array(p.length / 3).fill(-1);
    parts.forEach((part, k) => {
      const seen = new Set<number>();
      for (const t of part.triangles) for (let j = 0; j < 3; j++) seen.add(idx[t * 3 + j]);
      part.vertices = [...seen];
      for (const i of part.vertices) prep.partOf[i] = k;
      if (part.size > 0) return;
      // New: its middle and size, and whether it is moved whole.
      let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
      for (const i of part.vertices) {
        minX = Math.min(minX, p[i * 3]); maxX = Math.max(maxX, p[i * 3]);
        minY = Math.min(minY, p[i * 3 + 1]); maxY = Math.max(maxY, p[i * 3 + 1]);
        minZ = Math.min(minZ, p[i * 3 + 2]); maxZ = Math.max(maxZ, p[i * 3 + 2]);
      }
      part.centre = new Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
      part.size = Math.max(maxX - minX, maxY - minY, maxZ - minZ, 1e-3);
      part.small = part.size <= SMALL[this.build];
    });
    prep.parts = parts;
  }

  /**
   * Cut a mesh finely where crush `c` will fold it, coarser round about, and
   * leave the rest: in its own frame, from `toRoot`'s inverse.
   */
  private refine(prep: Prepared, c: Crush): void {
    const point = new Vector3(...c.point).applyMatrix4(rootInverseOf(toRoot));
    const push = new Vector3(...c.push).applyMatrix3(fromRoot).normalize();
    const fine = EDGE[this.build], bend = fine * BEND;
    const edge = (x: number, y: number, z: number) => {
      const rx = x - point.x, ry = y - point.y, rz = z - point.z;
      const s = rx * push.x + ry * push.y + rz * push.z;
      const r = Math.hypot(rx - push.x * s, ry - push.y * s, rz - push.z * s);
      if (r <= c.radius + 0.1 && s >= -0.3 && s <= c.zone + 0.1) return fine;
      if (r <= c.radius + 0.5 && s >= -0.6 && s <= c.zone + 0.5) return bend;
      return Infinity;
    };
    const geometry = prep.mesh.geometry;
    const mesh: TriMesh = {
      positions: (geometry.getAttribute('position') as BufferAttribute).array as Float32Array,
      indices: geometry.index!.array as Uint32Array,
    };
    const cut = tessellate(mesh, edge, 120000, prep.rest);
    if (cut.positions.length === mesh.positions.length) return;
    geometry.setAttribute('position', new BufferAttribute(cut.positions, 3));
    geometry.setIndex(new BufferAttribute(cut.indices, 1));
    prep.rest = cut.carry!;
    this.split(prep, cut);
  }

  /** Move vertex `i` by (dx, dy, dz) in its mesh's frame, never further than `limit` from where it began. */
  private move(pos: BufferAttribute, rest: Float32Array, i: number, dx: number, dy: number, dz: number, limit: number): void {
    let x = pos.getX(i) + dx - rest[i * 3], y = pos.getY(i) + dy - rest[i * 3 + 1], z = pos.getZ(i) + dz - rest[i * 3 + 2];
    const len = Math.hypot(x, y, z);
    if (len > limit) {
      x *= limit / len; y *= limit / len; z *= limit / len;
    }
    pos.setXYZ(i, rest[i * 3] + x, rest[i * 3 + 1] + y, rest[i * 3 + 2] + z);
  }

  /** Whether a small part stands out on the side scraped: a mirror along a wall. */
  private sticksOut(prep: Prepared, part: Part, zone: Zone): boolean {
    if (zone !== 'left' && zone !== 'right') return false;
    v.copy(part.centre).applyMatrix4(toRoot);
    return (zone === 'left' ? -v.x : v.x) > this.halfWidth - 0.15 && v.y > 0.6;
  }

  private breakLamp(prep: Prepared, part: Part): void {
    prep.mesh.material = prep.lamp === 'tail' ? brokenTail : brokenLamp;
    if (prep.lamp !== 'head') return;
    v.copy(part.centre).applyMatrix4(toRoot);
    // A headlamp at the front: its own side's beam goes out.
    if (v.z < 0) this.brokenBeams[v.x < 0 ? 0 : 1] = true;
  }

  /** Take a piece off its mesh and give it a mesh of its own, posed where it was. */
  private breakOff(prep: Prepared, part: Part): BrokenPiece {
    part.gone = true;
    const pos = prep.mesh.geometry.getAttribute('position') as BufferAttribute;
    const idx = prep.mesh.geometry.index!;
    // Its own copy of its triangles, about its middle.
    let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (const i of part.vertices) {
      minX = Math.min(minX, pos.getX(i)); maxX = Math.max(maxX, pos.getX(i));
      minY = Math.min(minY, pos.getY(i)); maxY = Math.max(maxY, pos.getY(i));
      minZ = Math.min(minZ, pos.getZ(i)); maxZ = Math.max(maxZ, pos.getZ(i));
    }
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2, cz = (minZ + maxZ) / 2;
    const out = new Float32Array(part.triangles.length * 9);
    part.triangles.forEach((t, n) => {
      for (let k = 0; k < 3; k++) {
        const i = idx.getX(t * 3 + k);
        out[n * 9 + k * 3] = pos.getX(i) - cx;
        out[n * 9 + k * 3 + 1] = pos.getY(i) - cy;
        out[n * 9 + k * 3 + 2] = pos.getZ(i) - cz;
      }
    });
    const geometry = new Geometry();
    geometry.setAttribute('position', new BufferAttribute(out, 3));
    geometry.computeVertexNormals();
    const piece = new Mesh(geometry, prep.mesh.material);
    piece.castShadow = true;
    piece.receiveShadow = true;
    piece.userData.debris = true;
    // Where it was in the scene.
    const at = new Vector3(cx, cy, cz).applyMatrix4(prep.mesh.matrixWorld);
    const turn = new Quaternion(), scale = new Vector3();
    prep.mesh.matrixWorld.decompose(new Vector3(), turn, scale);
    piece.position.copy(at);
    piece.quaternion.copy(turn);
    piece.scale.copy(scale);
    return {
      mesh: piece,
      half: [Math.max(0.01, ((maxX - minX) / 2) * scale.x), Math.max(0.01, ((maxY - minY) / 2) * scale.y), Math.max(0.01, ((maxZ - minZ) / 2) * scale.z)],
    };
  }

  /** Leave a mesh's broken-off pieces out of what it draws. */
  private dropGone(prep: Prepared): void {
    if (!prep.parts.some(p => p.gone && p.triangles.length > 0)) return;
    const geometry = prep.mesh.geometry;
    const idx = geometry.index!;
    const keep: number[] = [];
    for (const part of prep.parts) {
      if (part.gone) continue;
      for (const t of part.triangles) keep.push(idx.getX(t * 3), idx.getX(t * 3 + 1), idx.getX(t * 3 + 2));
    }
    const indices = new Uint32Array(keep);
    geometry.setIndex(new BufferAttribute(indices, 1));
    // The parts left, numbered afresh; the ones gone keep their story but no triangles.
    const gone = prep.parts.filter(p => p.gone);
    this.split(prep, { positions: (geometry.getAttribute('position') as BufferAttribute).array as Float32Array, indices });
    for (const part of gone) part.triangles = [];
  }
}
